import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { TimeInput24h } from '@/components/ui/time-input-24h';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useModules } from '@/contexts/ModulesContext';
import { 
  Plus, 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  CalendarDays,
  Clock, 
  User, 
  Bus, 
  Phone, 
  DollarSign, 
  Navigation,
  Search,
  RefreshCw,
  X,
  Map,
  Trash2,
  Edit,
  Circle,
  ChevronDown,
  ChevronUp,
  FileText,
  Route,
  Wallet,
  Mail,
  Table2,
  Bell
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import TripExcelView from './TripExcelView';
import TripPlanner from './TripPlanner';
import PushNotificationSettings from './PushNotificationSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// Format date to dd/mm/yyyy
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Custom 24-hour time input component using selects
export default function TripsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [driverFilter, setDriverFilter] = useState('');
  const [busFilter, setBusFilter] = useState('');
  
  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date();
    return formatDateLocal(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [dateTo, setDateTo] = useState(() => {
    const now = new Date();
    return formatDateLocal(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  });
  const [periodPreset, setPeriodPreset] = useState('thisMonth');
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [showExcelView, setShowExcelView] = useState(false);
  const [showTripPlanner, setShowTripPlanner] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const { isModuleEnabled } = useModules();

  useEffect(() => {
    fetchData();
  }, []);

  const handlePeriodPreset = (preset) => {
    setPeriodPreset(preset);
    const now = new Date();
    
    switch (preset) {
      case 'thisMonth':
        setDateFrom(formatDateLocal(new Date(now.getFullYear(), now.getMonth(), 1)));
        setDateTo(formatDateLocal(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
        break;
      case 'lastMonth':
        setDateFrom(formatDateLocal(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
        setDateTo(formatDateLocal(new Date(now.getFullYear(), now.getMonth(), 0)));
        break;
      default:
        break;
    }
  };

  const fetchData = async () => {
    try {
      const [tripsRes, busesRes, driversRes] = await Promise.all([
        axios.get(`${API}/trips`, getAuthHeaders()),
        axios.get(`${API}/buses?thumbnails_only=true`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/drivers`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setTrips(tripsRes.data);
      setBuses(busesRes.data || []);
      setDrivers(driversRes.data || []);
    } catch (error) {
      toast.error('Ошибка загрузки рейсов');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleDelete = async (tripId) => {
    if (!window.confirm('Удалить рейс?')) return;
    try {
      await axios.delete(`${API}/trips/${tripId}`, getAuthHeaders());
      toast.success('Рейс удалён');
      setSelectedTrip(null);
      fetchData();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const handleEdit = (trip) => {
    setEditingTrip(trip);
    setSelectedTrip(null);
  };

  const filteredTrips = trips.filter(trip => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = 
      trip.route_name?.toLowerCase().includes(searchLower) ||
      trip.driver_name?.toLowerCase().includes(searchLower) ||
      trip.bus_number?.toLowerCase().includes(searchLower) ||
      trip.start_location?.name?.toLowerCase().includes(searchLower) ||
      trip.end_location?.name?.toLowerCase().includes(searchLower);
    
    const matchesDriver = !driverFilter || trip.driver_id === driverFilter;
    const matchesBus = !busFilter || trip.bus_id === busFilter;
    
    // Date range filter
    let matchesDateRange = true;
    if (dateFrom && trip.date < dateFrom) matchesDateRange = false;
    if (dateTo && trip.date > dateTo) matchesDateRange = false;
    
    return matchesSearch && matchesDriver && matchesBus && matchesDateRange;
  });

  // Group by date and sort by time within each day
  const groupedTrips = {};
  filteredTrips.forEach(trip => {
    const date = trip.date;
    if (!groupedTrips[date]) groupedTrips[date] = [];
    groupedTrips[date].push(trip);
  });
  
  // Sort trips within each day by time_start
  Object.keys(groupedTrips).forEach(date => {
    groupedTrips[date].sort((a, b) => {
      const timeA = a.time_start || '00:00';
      const timeB = b.time_start || '00:00';
      return timeA.localeCompare(timeB);
    });
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-200 to-blue-100">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 shadow-xl">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-slate-300 hover:text-white px-2 sm:px-3"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline ml-2">Назад</span>
              </Button>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
                <MapPin className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
              </div>
              <h1 className="text-base sm:text-lg font-bold text-white">Рейсы</h1>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                className={`text-slate-300 hover:text-white p-2 ${refreshing ? 'animate-spin' : ''}`}
              >
                <RefreshCw className="w-5 h-5" />
              </Button>
              {isAdmin && (
                <>
                  {isModuleEnabled('trip_planner') && (
                    <Button
                      size="sm"
                      onClick={() => setShowTripPlanner(true)}
                      className="bg-purple-600 hover:bg-purple-700 px-2 sm:px-4"
                      title="Планировщик рейсов"
                    >
                      <CalendarDays className="w-4 h-4" />
                      <span className="hidden sm:inline ml-2">Планировщик</span>
                    </Button>
                  )}
                  {isModuleEnabled('excel_view') && (
                    <Button
                      size="sm"
                      onClick={() => setShowExcelView(true)}
                      className="bg-green-600 hover:bg-green-700 px-2 sm:px-4"
                      title="Excel-режим"
                    >
                      <Table2 className="w-4 h-4" />
                      <span className="hidden sm:inline ml-2">Excel</span>
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => setShowAddModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 px-2 sm:px-4"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline ml-2">Добавить</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Push Notifications Banner for drivers */}
        {user?.is_driver && (
          <div className="mb-4">
            <PushNotificationSettings />
          </div>
        )}

        {/* Filters */}
        <Card className="mb-4 sm:mb-6">
          <CardContent className="p-3 sm:p-4">
            {/* Quick Period Selection */}
            <div className="flex gap-2 sm:gap-3 mb-3 sm:mb-4">
              <button
                onClick={() => handlePeriodPreset('thisMonth')}
                className={`flex-1 py-2 sm:py-2.5 px-2 sm:px-4 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                  periodPreset === 'thisMonth'
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Текущий месяц
              </button>
              <button
                onClick={() => handlePeriodPreset('lastMonth')}
                className={`flex-1 py-2 sm:py-2.5 px-2 sm:px-4 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                  periodPreset === 'lastMonth'
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Прошлый месяц
              </button>
            </div>

            {/* Filters Grid */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
              {/* Driver Filter */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Водитель</label>
                <select
                  value={driverFilter}
                  onChange={(e) => setDriverFilter(e.target.value)}
                  className="w-full px-2 sm:px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="">Все</option>
                  {drivers.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.first_name || d.last_name ? `${d.first_name || ''} ${d.last_name || ''}`.trim() : d.username}
                    </option>
                  ))}
                </select>
              </div>

              {/* Bus Filter */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Машина</label>
                <select
                  value={busFilter}
                  onChange={(e) => setBusFilter(e.target.value)}
                  className="w-full px-2 sm:px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="">Все</option>
                  {buses.map(b => (
                    <option key={b.id} value={b.id}>{b.license_plate}</option>
                  ))}
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">С</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPeriodPreset('');
                  }}
                  className="w-full px-2 sm:px-3 py-2 border border-slate-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">По</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPeriodPreset('');
                  }}
                  className="w-full px-2 sm:px-3 py-2 border border-slate-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Search and Reset */}
            <div className="flex gap-2 sm:gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Поиск..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 sm:pl-10 text-sm"
                />
              </div>
              {(driverFilter || busFilter || searchQuery) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDriverFilter('');
                    setBusFilter('');
                    setSearchQuery('');
                  }}
                  className="px-2 sm:px-3"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Results count */}
            <div className="mt-2 sm:mt-3 text-xs sm:text-sm text-gray-500">
              Найдено: {filteredTrips.length}
            </div>
          </CardContent>
        </Card>

        {/* Trips List */}
        {Object.keys(groupedTrips).length === 0 ? (
          <Card className="p-6 sm:p-8">
            <div className="text-center text-slate-500">
              <MapPin className="w-10 sm:w-12 h-10 sm:h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm sm:text-base">Рейсы не найдены</p>
              {isAdmin && (
                <Button 
                  onClick={() => setShowAddModal(true)}
                  className="mt-4"
                  size="sm"
                >
                  Создать первый рейс
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {Object.entries(groupedTrips)
              .sort(([a], [b]) => new Date(b) - new Date(a))
              .map(([date, dateTrips]) => (
                <div key={date}>
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-xs sm:text-sm font-medium text-slate-600">
                      {formatDate(date)} — {new Date(date).toLocaleDateString('ru-RU', { weekday: 'short' })}
                    </span>
                  </div>
                  <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {dateTrips.map((trip) => {
                      const today = new Date().toISOString().split('T')[0];
                      const isPast = trip.date < today;
                      
                      return (
                        <Card
                          key={trip.id}
                          className={`relative cursor-pointer hover:shadow-lg transition-all active:scale-[0.98] ${isPast ? 'bg-gray-50' : ''}`}
                          onClick={() => setSelectedTrip(trip)}
                        >
                          {/* Edit & Delete buttons */}
                          {isAdmin && (
                            <div className="absolute top-2 right-2 flex gap-1 z-10">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(trip);
                                }}
                                className="p-1.5 sm:p-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white shadow-md transition-all"
                                title="Редактировать"
                              >
                                <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Удалить рейс "${trip.route_name || trip.date}"?`)) {
                                    handleDelete(trip.id);
                                  }
                                }}
                                className="p-1.5 sm:p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white shadow-md transition-all"
                                title="Удалить"
                              >
                                <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              </button>
                            </div>
                          )}
                          
                          <CardContent className="p-3 sm:p-4">
                            <div className="flex items-start justify-between mb-2 sm:mb-3">
                              <div className="flex-1 pr-8 sm:pr-10">
                                <div className="flex items-center gap-2 mb-1">
                                  {trip.time_start && (
                                    <span className="flex items-center gap-1 text-xs sm:text-sm text-slate-600">
                                      <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                      {trip.time_start}
                                    </span>
                                  )}
                                  {isPast && (
                                    <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                      ✓
                                    </span>
                                  )}
                                </div>
                                <h3 className="font-semibold text-sm sm:text-lg mt-1 line-clamp-1">
                                  {trip.route_name || 'Без названия'}
                                </h3>
                              </div>
                            </div>

                            {/* Route */}
                            <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm mb-2 sm:mb-3">
                              <div className="flex items-center gap-1 text-green-600 min-w-0">
                                <Navigation className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                                <span className="truncate">
                                  {trip.start_location?.name || '—'}
                                </span>
                              </div>
                              <span className="text-slate-300 shrink-0">→</span>
                              <div className="flex items-center gap-1 text-red-600 min-w-0">
                                <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                                <span className="truncate">
                                  {trip.end_location?.name || '—'}
                                </span>
                              </div>
                            </div>

                            {/* Info */}
                            <div className="flex items-center flex-wrap gap-2 sm:gap-3 text-xs text-slate-500">
                              {trip.driver_name && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                  <span className="truncate max-w-[80px] sm:max-w-none">{trip.driver_name}</span>
                                </span>
                              )}
                              {trip.bus_number && (
                                <span className="flex items-center gap-1">
                                  <Bus className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                  {trip.bus_number}
                                </span>
                              )}
                            </div>
                            
                            {/* Financial Info */}
                            {(trip.payment_amount > 0 || trip.driver_salary > 0) && (
                              <div className="flex items-center gap-2 sm:gap-3 mt-2 pt-2 border-t border-slate-100">
                                {trip.payment_amount > 0 && (
                                  <span className="flex items-center gap-1 text-xs sm:text-sm font-medium text-blue-600">
                                    <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    {trip.payment_amount}€
                                  </span>
                                )}
                                {trip.driver_salary > 0 && (
                                  <span className="flex items-center gap-1 text-xs sm:text-sm font-medium text-green-600">
                                    💰 {trip.driver_salary}€
                                  </span>
                                )}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Trip Detail Modal */}
      {selectedTrip && (
        <TripDetailModal
          trip={selectedTrip}
          isAdmin={isAdmin}
          onClose={() => setSelectedTrip(null)}
          onDelete={handleDelete}
          onEdit={handleEdit}
        />
      )}

      {/* Add Trip Modal */}
      {showAddModal && (
        <AddTripModal
          buses={buses}
          drivers={drivers}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchData();
          }}
        />
      )}

      {/* Edit Trip Modal */}
      {editingTrip && (
        <AddTripModal
          buses={buses}
          drivers={drivers}
          editTrip={editingTrip}
          onClose={() => setEditingTrip(null)}
          onSuccess={() => {
            setEditingTrip(null);
            fetchData();
          }}
        />
      )}

      {/* Excel View Modal */}
      {showExcelView && (
        <div className="fixed inset-0 z-50 bg-slate-900">
          <div className="absolute top-2 right-2 z-[1001]">
            <Button
              onClick={() => {
                setShowExcelView(false);
                fetchData();
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <X className="w-4 h-4 mr-2" />
              Закрыть Excel-режим
            </Button>
          </div>
          <TripExcelView drivers={drivers} buses={buses} isAdmin={isAdmin} />
        </div>
      )}

      {/* Trip Planner Modal */}
      {showTripPlanner && (
        <TripPlanner 
          onClose={() => {
            setShowTripPlanner(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
}

function TripDetailModal({ trip, isAdmin, onClose, onDelete, onEdit }) {
  // Автоматическое определение: прошедшая дата = завершён
  const today = new Date().toISOString().split('T')[0];
  const isPast = trip.date < today;

  const openInMaps = (location) => {
    // Приоритет: координаты > адрес
    if (location?.lat && location?.lng) {
      window.open(`https://www.google.com/maps?q=${location.lat},${location.lng}`, '_blank');
    } else if (location?.address) {
      window.open(`https://www.google.com/maps/search/${encodeURIComponent(location.address)}`, '_blank');
    } else if (location?.name) {
      window.open(`https://www.google.com/maps/search/${encodeURIComponent(location.name)}`, '_blank');
    } else {
      toast.error('Нет данных для открытия карты');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
      <div className="w-full sm:max-w-lg bg-white sm:rounded-2xl rounded-t-3xl shadow-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-300">
        {/* Header */}
        <div className="px-5 sm:px-6 py-4 border-b bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg sm:text-xl font-bold text-white">{trip.route_name || 'Рейс'}</h2>
                {isPast && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                    ✓ Завершён
                  </span>
                )}
              </div>
              <p className="text-sm text-white/80 mt-1 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(trip.date)}
              </p>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose} 
              className="rounded-full bg-white/10 hover:bg-white/20 text-white shrink-0"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4 space-y-4">
          {/* Description */}
          {trip.description && (
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-3 sm:p-4 border border-slate-200">
              <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                <FileText className="w-3 h-3" />
                Описание
              </p>
              <p className="text-sm text-slate-700">{trip.description}</p>
            </div>
          )}

          {/* Driver & Bus Cards */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl p-3 sm:p-4 text-white">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-white/70" />
                <span className="text-xs text-white/70">Водитель</span>
              </div>
              <p className="font-semibold text-sm sm:text-base truncate">{trip.driver_name || '—'}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl p-3 sm:p-4 text-white">
              <div className="flex items-center gap-2 mb-2">
                <Bus className="w-4 h-4 text-white/70" />
                <span className="text-xs text-white/70">Автобус</span>
              </div>
              <p className="font-semibold text-sm sm:text-base truncate">{trip.bus_number || '—'}</p>
            </div>
          </div>

          {/* Route Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-green-500 flex items-center justify-center">
                <Route className="w-3 h-3 text-white" />
              </div>
              <span className="font-medium text-slate-700 text-sm">Маршрут</span>
            </div>
            
            {/* Start */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-3 sm:p-4 border border-green-100">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                    <Navigation className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-green-600 font-medium">СТАРТ</span>
                      {trip.time_start && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">{trip.time_start}</span>
                      )}
                    </div>
                    <p className="font-medium text-slate-800 mt-1 text-sm sm:text-base truncate">{trip.start_location?.name || '—'}</p>
                    {trip.start_location?.address && (
                      <p className="text-xs sm:text-sm text-slate-500 truncate">{trip.start_location.address}</p>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openInMaps(trip.start_location)}
                  className="rounded-full hover:bg-green-100 shrink-0 w-9 h-9"
                >
                  <Map className="w-4 h-4 text-green-600" />
                </Button>
              </div>
            </div>

            {/* Waypoints */}
            {trip.stops && trip.stops.length > 0 && trip.stops.map((stop, index) => (
              <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-3 sm:p-4 border border-blue-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                      <Circle className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-blue-600 font-medium">ОСТАНОВКА {index + 1}</span>
                        {stop.arrival_time && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">{stop.arrival_time}</span>
                        )}
                      </div>
                      <p className="font-medium text-slate-800 mt-1 text-sm sm:text-base truncate">{stop.name || '—'}</p>
                      {stop.address && (
                        <p className="text-xs sm:text-sm text-slate-500 truncate">{stop.address}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openInMaps(stop)}
                    className="rounded-full hover:bg-blue-100 shrink-0 w-9 h-9"
                  >
                    <Map className="w-4 h-4 text-blue-600" />
                  </Button>
                </div>
              </div>
            ))}

            {/* End */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-3 sm:p-4 border border-red-100">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-red-500 flex items-center justify-center shrink-0">
                    <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-red-600 font-medium">ФИНИШ</span>
                      {trip.time_end && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">{trip.time_end}</span>
                      )}
                    </div>
                    <p className="font-medium text-slate-800 mt-1 text-sm sm:text-base truncate">{trip.end_location?.name || '—'}</p>
                    {trip.end_location?.address && (
                      <p className="text-xs sm:text-sm text-slate-500 truncate">{trip.end_location.address}</p>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openInMaps(trip.end_location)}
                  className="rounded-full hover:bg-red-100 shrink-0 w-9 h-9"
                >
                  <Map className="w-4 h-4 text-red-600" />
                </Button>
              </div>
            </div>
          </div>

          {/* Contact */}
          {(trip.contact_name || trip.contact_phone || trip.contact_email) && (
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-3 sm:p-4 border border-orange-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-lg bg-orange-500 flex items-center justify-center">
                  <Phone className="w-3 h-3 text-white" />
                </div>
                <span className="font-medium text-slate-700 text-sm">Контакт</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-800 text-sm sm:text-base truncate">{trip.contact_name || '—'}</p>
                  {trip.contact_phone && (
                    <p className="text-xs sm:text-sm text-slate-500">{trip.contact_phone}</p>
                  )}
                  {trip.contact_email && (
                    <p className="text-xs sm:text-sm text-slate-500 truncate">{trip.contact_email}</p>
                  )}
                </div>
                {trip.contact_phone && (
                  <a
                    href={`tel:${trip.contact_phone}`}
                    className="p-2.5 sm:p-3 bg-green-500 hover:bg-green-600 rounded-xl transition-colors shrink-0"
                  >
                    <Phone className="w-5 h-5 text-white" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Financial */}
          {(trip.payment_amount > 0 || trip.driver_salary > 0) && (
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-3 sm:p-4 border border-emerald-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <Wallet className="w-3 h-3 text-white" />
                </div>
                <span className="font-medium text-slate-700 text-sm">Финансы</span>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div className="text-center p-2 sm:p-3 bg-white rounded-lg">
                  <p className="text-xs text-slate-500">Оплата</p>
                  <p className="text-base sm:text-lg font-bold text-green-600">{trip.payment_amount || 0}€</p>
                </div>
                <div className="text-center p-2 sm:p-3 bg-white rounded-lg">
                  <p className="text-xs text-slate-500">ЗП</p>
                  <p className="text-base sm:text-lg font-bold text-blue-600">{trip.driver_salary || 0}€</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Admin Actions */}
        {isAdmin && (
          <div className="px-4 sm:px-6 py-4 border-t bg-slate-50 flex gap-3 safe-area-inset-bottom">
            <Button
              variant="outline"
              className="flex-1 border-slate-300 py-2.5"
              onClick={() => onEdit(trip)}
            >
              <Edit className="w-4 h-4 mr-2" />
              Изменить
            </Button>
            <Button
              variant="destructive"
              className="flex-1 py-2.5"
              onClick={() => onDelete(trip.id)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Удалить
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// Collapsible Section Component
const Section = ({ title, icon: Icon, iconColor, children, defaultOpen = true, badge }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
            isOpen 
              ? 'bg-gradient-to-r from-slate-100 to-slate-50 shadow-sm' 
              : 'bg-slate-50 hover:bg-slate-100'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColor}`}>
              <Icon className="w-4 h-4 text-white" />
            </div>
            <span className="font-medium text-slate-700">{title}</span>
            {badge && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {badge}
              </span>
            )}
          </div>
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-slideDown data-[state=closed]:animate-slideUp">
        <div className="pt-3 pb-1 px-1">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

function AddTripModal({ buses, drivers, onClose, onSuccess, editTrip = null }) {
  const isEditing = !!editTrip;
  const emailRef = useRef(null);
  
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const [formData, setFormData] = useState({
    date: editTrip?.date || '',
    bus_id: editTrip?.bus_id || '',
    driver_id: editTrip?.driver_id || '',
    route_name: editTrip?.route_name || '',
    start_location: { 
      name: editTrip?.start_location?.name || '', 
      address: editTrip?.start_location?.address || '', 
      coords: editTrip?.start_location?.lat && editTrip?.start_location?.lng 
        ? `${editTrip.start_location.lat}, ${editTrip.start_location.lng}` : '',
      time: editTrip?.time_start || ''
    },
    end_location: { 
      name: editTrip?.end_location?.name || '', 
      address: editTrip?.end_location?.address || '', 
      coords: editTrip?.end_location?.lat && editTrip?.end_location?.lng 
        ? `${editTrip.end_location.lat}, ${editTrip.end_location.lng}` : '',
      time: editTrip?.time_end || ''
    },
    description: editTrip?.description || '',
    contact_name: editTrip?.contact_name || '',
    contact_phone: editTrip?.contact_phone || '',
    contact_email: editTrip?.contact_email || '',
    payment_amount: editTrip?.payment_amount || '',
    driver_salary: editTrip?.driver_salary || ''
  });
  const [waypoints, setWaypoints] = useState(
    editTrip?.stops?.map(wp => ({
      name: wp.name || '',
      address: wp.address || '',
      coords: wp.lat && wp.lng ? `${wp.lat}, ${wp.lng}` : '',
      time: wp.arrival_time || ''
    })) || []
  );
  const [saving, setSaving] = useState(false);

  // Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await axios.get(`${API}/trip-templates`, getAuthHeaders());
        setTemplates(response.data);
      } catch (error) {
        console.error('Error loading templates:', error);
      }
    };
    loadTemplates();
  }, []);

  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    setFormData({
      ...formData,
      route_name: template.route_name || '',
      start_location: {
        name: template.start_location?.name || '',
        address: template.start_location?.address || '',
        coords: template.start_location?.lat && template.start_location?.lng 
          ? `${template.start_location.lat}, ${template.start_location.lng}` : '',
        time: template.start_location?.time || ''
      },
      end_location: {
        name: template.end_location?.name || '',
        address: template.end_location?.address || '',
        coords: template.end_location?.lat && template.end_location?.lng 
          ? `${template.end_location.lat}, ${template.end_location.lng}` : '',
        time: template.end_location?.time || ''
      },
      description: template.description || '',
      contact_name: template.contact_name || '',
      contact_phone: template.contact_phone || '',
      contact_email: template.contact_email || '',
      payment_amount: template.payment_amount || '',
      driver_salary: template.driver_salary || ''
    });

    if (template.stops?.length > 0) {
      setWaypoints(template.stops.map(wp => ({
        name: wp.name || '',
        address: wp.address || '',
        coords: wp.lat && wp.lng ? `${wp.lat}, ${wp.lng}` : '',
        time: wp.arrival_time || ''
      })));
    }

    toast.success(`Шаблон "${template.name}" применён`);
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Введите название шаблона');
      return;
    }

    try {
      const startCoords = parseCoords(formData.start_location.coords);
      const endCoords = parseCoords(formData.end_location.coords);

      const templateData = {
        name: templateName,
        route_name: formData.route_name,
        start_location: {
          name: formData.start_location.name,
          address: formData.start_location.address,
          lat: startCoords.lat,
          lng: startCoords.lng,
          time: formData.start_location.time
        },
        end_location: {
          name: formData.end_location.name,
          address: formData.end_location.address,
          lat: endCoords.lat,
          lng: endCoords.lng,
          time: formData.end_location.time
        },
        stops: waypoints.map(wp => {
          const coords = parseCoords(wp.coords);
          return { name: wp.name, address: wp.address, lat: coords.lat, lng: coords.lng, arrival_time: wp.time };
        }),
        description: formData.description,
        contact_name: formData.contact_name,
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email,
        payment_amount: parseFloat(formData.payment_amount) || 0,
        driver_salary: parseFloat(formData.driver_salary) || 0
      };

      await axios.post(`${API}/trip-templates`, templateData, getAuthHeaders());
      toast.success('Шаблон сохранён');
      setShowSaveTemplate(false);
      setTemplateName('');
      
      // Reload templates
      const response = await axios.get(`${API}/trip-templates`, getAuthHeaders());
      setTemplates(response.data);
    } catch (error) {
      toast.error('Ошибка сохранения шаблона');
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/trip-templates/${templateId}`, getAuthHeaders());
      setTemplates(templates.filter(t => t.id !== templateId));
      toast.success('Шаблон удалён');
    } catch (error) {
      toast.error('Ошибка удаления шаблона');
    }
  };

  const addWaypoint = () => {
    setWaypoints([...waypoints, { name: '', address: '', coords: '', time: '' }]);
  };

  const removeWaypoint = (index) => {
    setWaypoints(waypoints.filter((_, i) => i !== index));
  };

  const updateWaypoint = (index, field, value) => {
    const updated = [...waypoints];
    updated[index] = { ...updated[index], [field]: value };
    setWaypoints(updated);
  };

  const parseCoords = (coordsStr) => {
    if (!coordsStr) return { lat: null, lng: null };
    const parts = coordsStr.split(',').map(s => s.trim());
    if (parts.length === 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    return { lat: null, lng: null };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.date) {
      toast.error('Выберите дату');
      return;
    }
    if (!formData.driver_id) {
      toast.error('Выберите водителя');
      return;
    }
    if (!formData.start_location.time) {
      toast.error('Укажите время начала');
      return;
    }
    if (!formData.end_location.time) {
    }

    setSaving(true);
    try {
      // Parse waypoints
      const parsedWaypoints = waypoints
        .filter(wp => wp.name || wp.address || wp.coords || wp.time)
        .map(wp => {
          const { lat, lng } = parseCoords(wp.coords);
          return {
            name: wp.name,
            address: wp.address,
            lat,
            lng,
            arrival_time: wp.time
          };
        });

      const startCoords = parseCoords(formData.start_location.coords);
      const endCoords = parseCoords(formData.end_location.coords);

      const data = {
        ...formData,
        contact_email: formData.contact_email,
        time_start: formData.start_location.time,
        time_end: formData.end_location.time,
        start_location: {
          name: formData.start_location.name,
          address: formData.start_location.address,
          lat: startCoords.lat,
          lng: startCoords.lng
        },
        end_location: {
          name: formData.end_location.name,
          address: formData.end_location.address,
          lat: endCoords.lat,
          lng: endCoords.lng
        },
        stops: parsedWaypoints,
        payment_amount: parseFloat(formData.payment_amount) || 0,
        driver_salary: parseFloat(formData.driver_salary) || 0
      };

      if (isEditing) {
        await axios.put(`${API}/trips/${editTrip.id}`, data, getAuthHeaders());
        toast.success('Рейс обновлён');
      } else {
        await axios.post(`${API}/trips`, data, getAuthHeaders());
        toast.success('Рейс создан');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isEditing ? 'Ошибка обновления' : 'Ошибка создания'));
    } finally {
      setSaving(false);
    }
  };

  const updateLocation = (field, key, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: { ...prev[field], [key]: value }
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
      {/* Mobile: slide from bottom, Desktop: centered */}
      <div className="w-full sm:max-w-xl bg-white sm:rounded-2xl rounded-t-3xl shadow-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-300">
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white">
              {isEditing ? 'Редактирование рейса' : 'Новый рейс'}
            </h2>
            <p className="text-xs sm:text-sm text-blue-100 mt-0.5">
              {isEditing ? 'Измените данные рейса' : 'Заполните информацию о рейсе'}
            </p>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose} 
            className="rounded-full bg-white/10 hover:bg-white/20 text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Finance + Buttons - Always visible at top */}
        <div className="px-4 sm:px-6 py-3 border-b bg-gradient-to-r from-emerald-50 to-green-50">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Wallet className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-semibold text-emerald-700 text-sm">Финансы</span>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Оплата €</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.payment_amount}
                onChange={(e) => setFormData({ ...formData, payment_amount: e.target.value })}
                placeholder="0"
                className="border-emerald-200 bg-white"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">ЗП вод. €</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.driver_salary}
                onChange={(e) => setFormData({ ...formData, driver_salary: e.target.value })}
                placeholder="0"
                className="border-emerald-200 bg-white"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose} 
              className="flex-1 border-slate-300 py-2"
            >
              Отмена
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={saving} 
              className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 py-2 font-semibold"
            >
              {saving ? 'Сохранение...' : (isEditing ? 'Сохранить' : 'Создать')}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Templates */}
            {!isEditing && templates.length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-700 text-sm">Быстрое заполнение</span>
                </div>
                <div className="flex gap-2">
                  <select
                    value={selectedTemplate}
                    onChange={(e) => {
                      setSelectedTemplate(e.target.value);
                      if (e.target.value) applyTemplate(e.target.value);
                    }}
                    className="flex-1 border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Выберите шаблон...</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  {selectedTemplate && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteTemplate(selectedTemplate)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Section: Basic Info */}
            <Section title="Основная информация" icon={FileText} iconColor="bg-blue-500" defaultOpen={true}>
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Дата *</Label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Название маршрута</Label>
                    <Input
                      value={formData.route_name}
                      onChange={(e) => setFormData({ ...formData, route_name: e.target.value })}
                      placeholder="Vilnius - Kaunas"
                      className="border-slate-200 py-2.5"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Описание</Label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg resize-none text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={4}
                    placeholder="Дополнительная информация о рейсе..."
                  />
                </div>
              </div>
            </Section>

            {/* Section: Driver & Bus */}
            <Section title="Водитель и автобус" icon={User} iconColor="bg-purple-500" defaultOpen={true}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Водитель *</Label>
                  <select
                    value={formData.driver_id}
                    onChange={(e) => setFormData({ ...formData, driver_id: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Выберите</option>
                    {drivers.map(d => (
                      <option key={d.id} value={d.id}>
                        {d.first_name || d.last_name ? `${d.first_name || ''} ${d.last_name || ''}`.trim() : d.username}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Автобус</Label>
                  <select
                    value={formData.bus_id}
                    onChange={(e) => setFormData({ ...formData, bus_id: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Выберите</option>
                    {buses.map(b => (
                      <option key={b.id} value={b.id}>{b.number || b.license_plate}</option>
                    ))}
                  </select>
                </div>
              </div>
            </Section>

            {/* Section: Route */}
            <Section 
              title="Маршрут" 
              icon={Route} 
              iconColor="bg-green-500" 
              defaultOpen={true}
              badge={waypoints.length > 0 ? `${waypoints.length} остановок` : null}
            >
              <div className="space-y-4">
                {/* Start Location */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-3 sm:p-4 border border-green-100">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <Navigation className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-medium text-green-700 text-sm">Начальная точка *</span>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-green-600 mb-1 block">Время отправления *</Label>
                      <TimeInput24h
                        value={formData.start_location.time}
                        onChange={(val) => updateLocation('start_location', 'time', val)}
                      />
                    </div>
                    <Input
                      value={formData.start_location.name}
                      onChange={(e) => updateLocation('start_location', 'name', e.target.value)}
                      placeholder="Название"
                      className="border-green-200"
                    />
                    <Input
                      value={formData.start_location.address}
                      onChange={(e) => updateLocation('start_location', 'address', e.target.value)}
                      placeholder="Адрес"
                      className="border-green-200"
                    />
                    <div className="flex gap-2">
                      <Input
                        value={formData.start_location.coords}
                        onChange={(e) => updateLocation('start_location', 'coords', e.target.value)}
                        placeholder="Координаты"
                        className="flex-1 border-green-200 text-xs"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="border-green-300 hover:bg-green-50 shrink-0"
                        onClick={() => {
                          const { lat, lng } = parseCoords(formData.start_location.coords);
                          if (lat && lng) {
                            window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
                          } else if (formData.start_location.address) {
                            window.open(`https://www.google.com/maps/search/${encodeURIComponent(formData.start_location.address)}`, '_blank');
                          } else if (formData.start_location.name) {
                            window.open(`https://www.google.com/maps/search/${encodeURIComponent(formData.start_location.name)}`, '_blank');
                          } else {
                            toast.error('Введите координаты или адрес');
                          }
                        }}
                      >
                        <Map className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Waypoints */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                        <Circle className="w-3 h-3 text-white" />
                      </div>
                      <span className="font-medium text-blue-700 text-sm">Промежуточные</span>
                    </div>
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline" 
                      onClick={addWaypoint}
                      className="border-blue-300 text-blue-600 hover:bg-blue-50 text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Добавить
                    </Button>
                  </div>
                  
                  {waypoints.map((wp, index) => (
                    <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-3 sm:p-4 border border-blue-100">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-medium text-blue-700">Остановка {index + 1}</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeWaypoint(index)}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <TimeInput24h
                          value={wp.time}
                          onChange={(val) => updateWaypoint(index, 'time', val)}
                        />
                        <Input
                          value={wp.name}
                          onChange={(e) => updateWaypoint(index, 'name', e.target.value)}
                          placeholder="Название"
                          className="border-blue-200"
                        />
                        <Input
                          value={wp.address}
                          onChange={(e) => updateWaypoint(index, 'address', e.target.value)}
                          placeholder="Адрес"
                          className="border-blue-200"
                        />
                        <div className="flex gap-2">
                          <Input
                            value={wp.coords}
                            onChange={(e) => updateWaypoint(index, 'coords', e.target.value)}
                            placeholder="Координаты"
                            className="flex-1 border-blue-200 text-xs"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="border-blue-300 hover:bg-blue-50 shrink-0"
                            onClick={() => {
                              const { lat, lng } = parseCoords(wp.coords);
                              if (lat && lng) {
                                window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
                              } else if (wp.address) {
                                window.open(`https://www.google.com/maps/search/${encodeURIComponent(wp.address)}`, '_blank');
                              } else if (wp.name) {
                                window.open(`https://www.google.com/maps/search/${encodeURIComponent(wp.name)}`, '_blank');
                              } else {
                                toast.error('Введите координаты или адрес');
                              }
                            }}
                          >
                            <Map className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* End Location */}
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-3 sm:p-4 border border-red-100">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                      <MapPin className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-medium text-red-700 text-sm">Конечная точка *</span>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-red-600 mb-1 block">Время прибытия</Label>
                      <TimeInput24h
                        value={formData.end_location.time}
                        onChange={(val) => updateLocation('end_location', 'time', val)}
                      />
                    </div>
                    <Input
                      value={formData.end_location.name}
                      onChange={(e) => updateLocation('end_location', 'name', e.target.value)}
                      placeholder="Название"
                      className="border-red-200"
                    />
                    <Input
                      value={formData.end_location.address}
                      onChange={(e) => updateLocation('end_location', 'address', e.target.value)}
                      placeholder="Адрес"
                      className="border-red-200"
                    />
                    <div className="flex gap-2">
                      <Input
                        value={formData.end_location.coords}
                        onChange={(e) => updateLocation('end_location', 'coords', e.target.value)}
                        placeholder="Координаты"
                        className="flex-1 border-red-200 text-xs"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="border-red-300 hover:bg-red-50 shrink-0"
                        onClick={() => {
                          const { lat, lng } = parseCoords(formData.end_location.coords);
                          if (lat && lng) {
                            window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
                          } else if (formData.end_location.address) {
                            window.open(`https://www.google.com/maps/search/${encodeURIComponent(formData.end_location.address)}`, '_blank');
                          } else if (formData.end_location.name) {
                            window.open(`https://www.google.com/maps/search/${encodeURIComponent(formData.end_location.name)}`, '_blank');
                          } else {
                            toast.error('Введите координаты или адрес');
                          }
                        }}
                      >
                        <Map className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Section>

            {/* Section: Contact */}
            <Section title="Контактная информация" icon={Phone} iconColor="bg-orange-500" defaultOpen={false}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Имя</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Иван"
                    className="border-slate-200"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Телефон</Label>
                  <Input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="+370..."
                    className="border-slate-200"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Email</Label>
                  <input
                    ref={emailRef}
                    type="text"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    onPaste={(e) => {
                      // Delay to ensure pasted content is in the input
                      setTimeout(() => {
                        if (emailRef.current) {
                          setFormData(prev => ({ ...prev, contact_email: emailRef.current.value }));
                        }
                      }, 10);
                    }}
                    placeholder="email@..."
                    className="flex h-10 w-full rounded-md border border-slate-200 bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
              </div>
            </Section>

            {/* Save as Template */}
            {!isEditing && (
              <div className="bg-slate-50 rounded-xl p-3 sm:p-4 border border-slate-200">
                {!showSaveTemplate ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-slate-600 hover:text-slate-800 hover:bg-slate-200"
                    onClick={() => setShowSaveTemplate(true)}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Сохранить как шаблон
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="Название шаблона"
                      className="flex-1 border-slate-300"
                    />
                    <Button type="button" onClick={saveAsTemplate} className="bg-blue-600 hover:bg-blue-700 shrink-0">
                      OK
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setShowSaveTemplate(false)} className="shrink-0">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
